import { formatAddressLocation, hasStoreAddress, isCoordinateString, fillMissingAddressFields, normalizeStoreStatus } from "./storeAddress";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { FiMap } from "react-icons/fi";
import { LuSatellite } from "react-icons/lu";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import ModalCloseButton from "../common/ModalCloseButton";
import CommonButton from "../common/CommonButton";
import ReactSelector from "../common/CommonReactSelector";
import useDirection from "../../hooks/useDirection";

const storePin = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});
const addressSearchCache = new Map();
let lastAddressSearchAt = 0;
const emptyStoreAddress = {
  governorate: "",
  wilayat: "",
  city: "",
  area: "",
  street: "",
  building: "",
};
const omanGovernorates = [
  "Muscat", "Dhofar", "Musandam", "Al Buraimi", "Al Dakhiliyah", "Al Dhahirah",
  "Al Wusta", "North Al Batinah", "South Al Batinah", "North Ash Sharqiyah", "South Ash Sharqiyah",
];

const geocodeAddress = async (fields, query, signal) => {
  const parts = [
    fields?.building,
    fields?.street,
    fields?.area,
    fields?.city,
    fields?.wilayat,
    fields?.governorate,
    fields?.country,
  ]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter(Boolean);

  if (
    fields?.governorate ||
    fields?.city ||
    fields?.wilayat ||
    fields?.area ||
    fields?.street
  ) {
    try {
      const structured = new URLSearchParams({ limit: "1" });
      if (fields.governorate?.trim()) structured.set("state", fields.governorate.trim());
      if (fields.wilayat?.trim()) structured.set("county", fields.wilayat.trim());
      if (fields.city?.trim()) structured.set("city", fields.city.trim());
      if (fields.area?.trim()) structured.set("district", fields.area.trim());
      if (fields.street?.trim()) structured.set("street", fields.street.trim());
      if (fields.building?.trim()) structured.set("housenumber", fields.building.trim());

      const res = await fetch(`https://photon.komoot.io/structured?${structured}`, { signal });
      if (res.ok) {
        const data = await res.json();
        const coords = data.features?.[0]?.geometry?.coordinates;
        if (coords && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
          return { lon: coords[0], lat: coords[1], country: data.features[0].properties?.country || "", countryCode: data.features[0].properties?.countrycode || "" };
        }
      }
    } catch (e) {
      if (e.name === "AbortError") throw e;
    }
  }

  const queriesToTry = [];
  if (parts.length > 0) {
    queriesToTry.push(parts.join(", "));

  }
  if (query && query.trim()) {
    const qStr = query.trim();
    queriesToTry.unshift(qStr);
  }

  for (const q of queriesToTry) {
    try {
      const params = new URLSearchParams({ q, limit: "1" });
      const res = await fetch(`https://photon.komoot.io/api?${params}`, { signal });
      if (res.ok) {
        const data = await res.json();
        const coords = data.features?.[0]?.geometry?.coordinates;
        if (coords && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
          return { lon: coords[0], lat: coords[1], country: data.features[0].properties?.country || "", countryCode: data.features[0].properties?.countrycode || "" };
        }
      }
    } catch (e) {
      if (e.name === "AbortError") throw e;
    }
  }

  for (const q of queriesToTry.slice(0, 2)) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&q=${encodeURIComponent(q)}`,
        { signal, headers: { Accept: "application/json" } }
      );
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0 && data[0].lat && data[0].lon) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            return { lat, lon, country: data[0].address?.country || "", countryCode: data[0].address?.country_code || "" };
          }
        }
      }
    } catch (e) {
      if (e.name === "AbortError") throw e;
    }
  }

  return null;
};

const normalizeGovernorate = (raw) => {
  if (!raw) return "";
  const cleaned = raw.toLowerCase().replace(/governorate|محافظة/gi, "").trim();
  const match = omanGovernorates.find(
    (g) => g.toLowerCase() === cleaned || g.toLowerCase().includes(cleaned) || cleaned.includes(g.toLowerCase())
  );
  return match || raw.trim();
};

const reverseCache = new Map();

export const reverseGeocode = async (lat, lon, langMode, signal) => {
  const numLat = Number(lat);
  const numLon = Number(lon);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLon)) return null;

  const lang = langMode === "ur" ? "en" : langMode || "en";
  const cacheKey = `${numLat.toFixed(5)},${numLon.toFixed(5)},${lang}`;
  if (reverseCache.has(cacheKey)) {
    return reverseCache.get(cacheKey);
  }

  let primaryAddress = null;
  try {
    const res = await fetch(`https://photon.komoot.io/reverse?lat=${numLat}&lon=${numLon}&lang=${lang}`, { signal });
    if (res.ok) {
      const data = await res.json();
      const feature = data.features?.[0];
      if (feature && feature.properties) {
        const props = feature.properties;
        const governorate = normalizeGovernorate(props.state || props.region || props.province || "");
        const wilayat = (props.county || props.state_district || "").replace(/\s*Province/i, "").replace(/\s*ولاية/i, "").trim();
        const city = props.city || props.town || props.village || props.hamlet || props.locality || "";
        const area = props.district || props.suburb || props.neighbourhood || props.quarter || "";
        const street = props.street || (props.type === "street" ? props.name : "") || "";
        const building = props.housenumber || (props.type === "house" && props.name !== street ? props.name : "") || "";
        const postalCode = props.postcode || "";

        primaryAddress = {
          governorate: governorate || props.state || "",
          wilayat: wilayat || "",
          city: city || "",
          area: area || "",
          street: street || "",
          building: building || "",
          country: props.country || "",
          countryCode: props.countrycode || "",
          postalCode: postalCode || "",
        };
        if (hasStoreAddress(primaryAddress)) {
          reverseCache.set(cacheKey, primaryAddress);
          return primaryAddress;
        }
      }
    }
  } catch (e) {
    if (e.name === "AbortError") throw e;
  }

  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${numLat}&lon=${numLon}&accept-language=${lang}`,
      { signal, headers: { Accept: "application/json" } }
    );
    if (res.ok) {
      const data = await res.json();
      const addr = data.address || {};
      const governorate = normalizeGovernorate(addr.state || addr.region || addr.province || "");
      const wilayat = (addr.county || addr.state_district || addr.province || "").replace(/\s*(Province|Governorate)/i, "").replace(/\s*ولاية/i, "").trim();
      const city = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet || "";
      const area = addr.suburb || addr.neighbourhood || addr.quarter || addr.residential || "";
      const street = addr.road || addr.street || addr.pedestrian || "";
      const building = addr.house_number || addr.building || "";
      const postalCode = addr.postcode || "";

      const resolved = fillMissingAddressFields(primaryAddress, {
        governorate: governorate || addr.state || "",
        wilayat: wilayat || "",
        city: city || "",
        area: area || "",
        street: street || "",
        building: building || "",
        country: addr.country || "",
        countryCode: addr.country_code || "",
        postalCode: postalCode || "",
      });
      reverseCache.set(cacheKey, resolved);
      return resolved;
    }
  } catch (e) {
    if (e.name === "AbortError") throw e;
  }

  if (primaryAddress) {
    reverseCache.set(cacheKey, primaryAddress);
  }
  return primaryAddress;
};

export { formatAddressLocation } from "./storeAddress";

const MapLocationPicker = ({ onSelect }) => {
  const map = useMapEvents({
    click(event) {
      const point = map.wrapLatLng(event.latlng);
      onSelect(point.lat.toFixed(6), point.lng.toFixed(6));
    },
  });
  return null;
};

const ResizeMapOnOpen = () => {
  const map = useMap();
  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 250);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
};

const MapZoomWatcher = ({ onZoomChange }) => {
  useMapEvents({ zoomend(event) { onZoomChange(event.target.getZoom()); } });
  return null;
};

const MapCenterController = ({ center }) => {
  const map = useMap();
  const lastCenterRef = useRef(null);
  useEffect(() => {
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      const key = `${center[0].toFixed(5)},${center[1].toFixed(5)}`;
      if (lastCenterRef.current !== key) {
        lastCenterRef.current = key;
        map.setView(center, Math.max(map.getZoom(), 13));
      }
    }
  }, [center, map]);
  return null;
};

const CreateStoreModal = ({ open, onClose, onCreateStore, onUpdateStore, initialStore, isViewOnly = false }) => {
  const { theme, langMode } = useContext(AppContext);
  const { isRTL } = useDirection();
  const t = translations[langMode] || {};
  const isEditing = Boolean(initialStore) && !isViewOnly;
  const [storeName, setStoreName] = useState("");
  const [storeStatus, setStoreStatus] = useState("active");
  const statusOptions = [
    { value: "active", label: t.storeActive || "Active" },
    { value: "inactive", label: t.storeInactive || "Inactive" },
  ];
  const [locationMode, setLocationMode] = useState("address");
  const [addressFields, setAddressFields] = useState(emptyStoreAddress);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressSearchError, setAddressSearchError] = useState("");
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [locationOrigin, setLocationOrigin] = useState("none");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [storeNameError, setStoreNameError] = useState("");
  const createAbortRef = useRef(null);
  const reverseAbortRef = useRef(null);
  const [mapType, setMapType] = useState("map");
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(5);
  const dark = theme === "dark";
  const highContrast = theme === "high-contrast";
  const fieldClass = highContrast
    ? "border-white bg-black text-white"
    : dark
      ? "border-slate-600 bg-slate-800 text-white"
      : "border-slate-300 bg-white text-slate-800";
  const lat = Number(latitude);
  const lng = Number(longitude);
  const hasLocation = latitude !== "" && longitude !== "" && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

  useEffect(() => {
    if (!open) return;

    setAddressFields(emptyStoreAddress);
    setAddressQuery("");
    reverseAbortRef.current?.abort();
    if (initialStore) {
      setStoreName(initialStore.name || initialStore.storeName || "");
      setStoreStatus(normalizeStoreStatus(initialStore.storeStatus ?? initialStore.status));
      const numLat = Number(initialStore.latitude);
      const numLng = Number(initialStore.longitude);
      const hasCoords =
        initialStore.latitude != null &&
        initialStore.longitude != null &&
        Number.isFinite(numLat) &&
        Number.isFinite(numLng);

      if (hasCoords) {
        setLatitude(numLat.toFixed(6));
        setLongitude(numLng.toFixed(6));
        setLocationOrigin("stored");
        setZoom(14);
      } else {
        setLatitude("");
        setLongitude("");
        setLocationOrigin("none");
        setZoom(5);
      }

      if (hasStoreAddress(initialStore.address)) {
        setAddressFields({
          country: initialStore.address.country || "",
          countryCode: initialStore.address.countryCode || "",
          governorate: initialStore.address.governorate || "",
          wilayat: initialStore.address.wilayat || "",
          city: initialStore.address.city || "",
          area: initialStore.address.area || "",
          street: initialStore.address.street || "",
          building: initialStore.address.building || "",
        });
        setLocationMode("address");
      } else if (initialStore.location && !isCoordinateString(initialStore.location)) {
        setAddressQuery(initialStore.location);
        setLocationMode("address");
      } else if (hasCoords && !hasStoreAddress(initialStore.address)) {
        setLocationMode("coordinates");
      } else {
        setLocationMode("address");
      }
    } else {
      setStoreName("");
      setStoreStatus("active");
      setLocationMode("address");
      setAddressFields(emptyStoreAddress);
      setAddressQuery("");
      setLatitude("");
      setLongitude("");
      setLocationOrigin("none");
      setZoom(5);
    }

    setAddressResults([]);
    setAddressSearching(false);
    setAddressSearchError("");
    setResolvingLocation(false);
    setReverseGeocoding(false);
    setCreating(false);
    setCreateError("");
    setStoreNameError("");
  }, [open, initialStore]);

  useEffect(() => {
    const query = addressQuery.trim();
    if (isViewOnly || !open || locationMode !== "address" || query.length < 3) {
      setAddressSearching(false);
      setAddressResults([]);
      return undefined;
    }
    const cacheKey = `${langMode}:${query.toLowerCase()}`;
    if (addressSearchCache.has(cacheKey)) {
      setAddressResults(addressSearchCache.get(cacheKey));
      setAddressSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    setAddressSearching(true);
    setAddressSearchError("");
    const timer = window.setTimeout(async () => {
      try {
        const waitMs = Math.max(0, 950 - (Date.now() - lastAddressSearchAt));
        if (waitMs) await new Promise((resolve) => window.setTimeout(resolve, waitMs));
        if (cancelled) return;
        lastAddressSearchAt = Date.now();
        const params = new URLSearchParams({ q: query, limit: "5", lang: langMode === "ur" ? "en" : langMode });
        const response = await fetch(`https://photon.komoot.io/api?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error("Address search failed");
        const data = await response.json();
        if (cancelled) return;
        const results = (data.features || []).map((feature) => {
          const details = feature.properties || {};
          const [lon, lat] = feature.geometry?.coordinates || [];
          return {
            place_id: `${details.osm_type || "place"}-${details.osm_id || `${lat}-${lon}`}`,
            lat,
            lon,
            name: details.name,
            display_name: [details.name, details.street, details.city, details.county, details.state, details.country].filter(Boolean).filter((part, index, parts) => parts.indexOf(part) === index).join(", "),
            address: {
              country: details.country,
              country_code: details.countrycode,
              state: details.state,
              county: details.county,
              city: details.city,
              suburb: details.district,
              road: details.street,
              house_number: details.housenumber,
              postcode: details.postcode,
            },
          };
        });
        addressSearchCache.set(cacheKey, results);
        setAddressResults(results);
      } catch (error) {
        if (!cancelled && error.name !== "AbortError") setAddressSearchError(t.storeAddressSearchFailed || "Could not search addresses. Please try again.");
      } finally {
        if (!cancelled) setAddressSearching(false);
      }
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [isViewOnly, addressQuery, langMode, locationMode, open, t.storeAddressSearchFailed]);

  // Real-time automatic geocoding when address fields are modified
  useEffect(() => {
    if (isViewOnly || !open || locationMode !== "address") {
      setResolvingLocation(false);
      return undefined;
    }
    const hasAnyAddress = [
      addressFields.governorate,
      addressFields.wilayat,
      addressFields.city,
      addressFields.area,
      addressFields.street,
      addressFields.building,
    ].some((p) => p && p.trim().length > 0);

    if (!hasAnyAddress || locationOrigin !== "manual") {
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setResolvingLocation(true);

    const timer = window.setTimeout(async () => {
      try {
        const coords = await geocodeAddress(addressFields, addressQuery, controller.signal);
        if (cancelled || !coords) return;
        const nextLat = coords.lat;
        const nextLng = coords.lon;
        if (Number.isFinite(nextLat) && Number.isFinite(nextLng)) {
          setLatitude(nextLat.toFixed(6));
          setLongitude(nextLng.toFixed(6));
          map?.flyTo([nextLat, nextLng], 13);
        }
      } catch (err) {
        // ignore abort
      } finally {
        if (!cancelled) setResolvingLocation(false);
      }
    }, 600);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
      setResolvingLocation(false);
    };
  }, [isViewOnly, addressFields, addressQuery, locationMode, locationOrigin, map, open]);

  const handleClose = () => {
    createAbortRef.current?.abort();
    createAbortRef.current = null;
    reverseAbortRef.current?.abort();
    reverseAbortRef.current = null;
    setReverseGeocoding(false);
    setStoreName("");
    setStoreStatus("active");
    setLocationMode("address");
    setAddressFields(emptyStoreAddress);
    setAddressQuery("");
    setAddressResults([]);
    setAddressSearching(false);
    setAddressSearchError("");
    setResolvingLocation(false);
    setLatitude("");
    setLongitude("");
    setLocationOrigin("none");
    setCreating(false);
    setCreateError("");
    setStoreNameError("");
    setMapType("map");
    setZoom(5);
    onClose();
  };

  const handleMapLocationSelect = async (nextLat, nextLng) => {
    if (isViewOnly) return;
    setLatitude(nextLat);
    setLongitude(nextLng);
    setAddressFields(emptyStoreAddress);
    setLocationOrigin("map");
    setAddressQuery("");
    setAddressResults([]);
    setCreateError("");

    reverseAbortRef.current?.abort();
    const controller = new AbortController();
    reverseAbortRef.current = controller;
    setReverseGeocoding(true);

    try {
      const addr = await reverseGeocode(nextLat, nextLng, langMode, controller.signal);
      if (addr) {
        if (!controller.signal.aborted) setAddressFields({ ...emptyStoreAddress, ...addr });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Reverse geocoding error:", err);
      }
    } finally {
      if (reverseAbortRef.current === controller) {
        reverseAbortRef.current = null;
        setReverseGeocoding(false);
      }
    }
  };

  useEffect(() => {
    if (isViewOnly || !open || !hasLocation || locationOrigin !== "coordinates") return;
    const timer = setTimeout(() => handleMapLocationSelect(lat, lng), 600);
    return () => clearTimeout(timer);
    // The lookup is driven only by coordinate edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewOnly, open, latitude, longitude, locationOrigin]);

  const selectAddress = (result) => {
    reverseAbortRef.current?.abort();
    setReverseGeocoding(false);
    const nextLat = Number(result.lat);
    const nextLng = Number(result.lon);
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;
    reverseAbortRef.current?.abort();
    const found = result.address || {};
    setAddressFields({
      country: found.country || "",
      countryCode: found.country_code || "",
      governorate: found.state || found.region || "",
      wilayat: found.county || found.state_district || found.municipality || "",
      city: found.city || found.town || found.village || found.hamlet || found.locality || "",
      area: found.suburb || found.neighbourhood || found.quarter || found.city_district || found.residential || "",
      street: found.road || found.pedestrian || found.street || "",
      building: found.house_name || found.building || found.house_number || "",
    });
    setAddressQuery("");
    setLatitude(nextLat.toFixed(6));
    setLongitude(nextLng.toFixed(6));
    setLocationOrigin("search");
    setAddressResults([]);
    setCreateError("");
    map?.flyTo([nextLat, nextLng], 14);
  };

  const updateAddressField = (name, value) => {
    reverseAbortRef.current?.abort();
    setAddressFields((previous) => ({ ...previous, [name]: value }));
    setLocationOrigin("manual");
    setLatitude("");
    setLongitude("");
    setCreateError("");
  };

  const handleCreate = async () => {
    if (isViewOnly || creating || reverseGeocoding) return;
    if (!storeName.trim()) {
      setStoreNameError(t.storeNameRequired || "Store name is required.");
      return;
    }
    if (storeName.trim().length > 255) {
      setStoreNameError(t.storeNameMaxLength || "Store name must not exceed 255 characters.");
      return;
    }
    setStoreNameError("");
    setCreateError("");
    let resolvedAddress = { ...addressFields };
    let nextLat = Number(latitude);
    let nextLng = Number(longitude);

    if (locationMode === "address") {
      const parts = [
        addressFields.building,
        addressFields.street,
        addressFields.area,
        addressFields.city,
        addressFields.wilayat,
        addressFields.governorate,
      ]
        .filter(Boolean)
        .map((s) => String(s).trim())
        .filter(Boolean);

      const hasAddressText = parts.length > 0 || addressQuery.trim().length > 0;
      if (!hasAddressText && !hasLocation) {
        setCreateError(t.storeAddressRequired || "Please enter an address or search for a location.");
        return;
      }

      // If coordinates are not yet resolved or address was manually modified, automatically geocode now
      if (!hasLocation || locationOrigin === "manual") {
        const controller = new AbortController();
        createAbortRef.current = controller;
        setCreating(true);
        try {
          const coords = await geocodeAddress(addressFields, addressQuery, controller.signal);
          if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
            setCreateError(t.storeAddressNotFound || "Could not find coordinates for this address. Please refine address or select on map.");
            setCreating(false);
            return;
          }
          resolvedAddress = { ...resolvedAddress, country: coords.country, countryCode: coords.countryCode };
          nextLat = coords.lat;
          nextLng = coords.lon;
          setLatitude(nextLat.toFixed(6));
          setLongitude(nextLng.toFixed(6));
          setLocationOrigin("auto");
          map?.flyTo([nextLat, nextLng], 14);
        } catch (error) {
          if (error.name !== "AbortError") {
            setCreateError(t.storeAddressSearchFailed || "Could not search addresses. Please try again.");
          }
          setCreating(false);
          return;
        } finally {
          if (createAbortRef.current === controller) createAbortRef.current = null;
        }
      }
    }

    if (
      !Number.isFinite(nextLat) ||
      !Number.isFinite(nextLng) ||
      Math.abs(nextLat) > 90 ||
      Math.abs(nextLng) > 180 ||
      (locationMode === "coordinates" && !hasLocation)
    ) {
      setCreateError(t.storeCoordinatesRequired || "Valid coordinates (latitude and longitude) are required.");
      setCreating(false);
      return;
    }


    if (!hasStoreAddress(resolvedAddress) && !addressQuery.trim()) {
      setCreating(true);
      const controller = new AbortController();
      createAbortRef.current = controller;
      try {
        resolvedAddress = await reverseGeocode(nextLat, nextLng, langMode, controller.signal);
        if (controller.signal.aborted) return;
        if (!hasStoreAddress(resolvedAddress)) {
          setCreateError(t.storeAddressNotFound || "Could not resolve this location. Please enter an address or try again.");
          return;
        }
        setAddressFields({ ...emptyStoreAddress, ...resolvedAddress });
      } catch (error) {
        if (error.name !== "AbortError") setCreateError(t.storeAddressNotFound || "Could not resolve this location. Please try again.");
        return;
      } finally {
        setCreating(false);
        if (createAbortRef.current === controller) createAbortRef.current = null;
      }
    }
    const location = hasStoreAddress(resolvedAddress)
      ? formatAddressLocation(resolvedAddress)
      : addressQuery.trim();

    const normalizedStatus = normalizeStoreStatus(storeStatus || initialStore?.storeStatus || initialStore?.status);
    const storePayload = {
      id: initialStore?.id || `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      name: storeName.trim(),
      storeName: storeName.trim(),
      location,
      status: normalizedStatus,
      storeStatus: normalizedStatus === "inactive" ? "INACTIVE" : "ACTIVE",
      latitude: nextLat,
      longitude: nextLng,
      address: { ...resolvedAddress },
    };

    try {
      setCreating(true);
      if (initialStore && onUpdateStore) {
        await onUpdateStore(storePayload);
      } else if (onCreateStore) {
        await onCreateStore(storePayload);
      }
      setCreating(false);
      handleClose();
    } catch (err) {
      setCreating(false);
      setCreateError(err.response?.data?.message || err.message || "Failed to save store.");
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1200]" onClose={handleClose} dir={isRTL ? "rtl" : "ltr"}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-slate-900/50" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-3 sm:p-5">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className={`my-auto flex max-h-[90dvh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border shadow-2xl ${highContrast ? "border-white bg-black text-white" : dark ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800"}`}>
              <div className={`flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-5 ${highContrast ? "border-white" : dark ? "border-slate-700" : "border-slate-200"}`}>
                <Dialog.Title className="text-lg font-semibold">{isViewOnly ? (t.storeDetails || "Store Details") : isEditing ? (t.updateStoreTitle || "Update Store") : t.createStore}</Dialog.Title>
                <ModalCloseButton onClick={handleClose} ariaLabel={t.cancel} />
              </div>
              <div className="min-h-0 overflow-y-auto p-4 sm:p-5">
                {!isViewOnly && <p className={`mb-3 text-sm ${highContrast ? "text-white" : dark ? "text-slate-300" : "text-slate-500"}`}>{locationMode === "address" ? t.selectStoreAddress : t.selectStoreLocation}</p>}
                <div className="relative h-[42dvh] min-h-[280px] max-h-[520px] w-full overflow-hidden rounded-lg border border-slate-200 sm:h-[50dvh]" dir="ltr">
                  <MapContainer ref={setMap} center={[21.512, 55.923]} zoom={5} zoomControl={false} worldCopyJump scrollWheelZoom={true} className="h-full w-full" style={{ zIndex: 0 }}>
                    {mapType === "map" ? (
                      <TileLayer attribution="© OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    ) : (
                      <TileLayer attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>' url="https://mt.google.com/vt/lyrs=y&x={x}&y={y}&z={z}" />
                    )}
                    <ResizeMapOnOpen />
                    <MapZoomWatcher onZoomChange={setZoom} />
                    {!isViewOnly && <MapLocationPicker onSelect={handleMapLocationSelect} />}
                    <MapCenterController center={hasLocation ? [lat, lng] : null} />
                    {hasLocation && <Marker position={[lat, lng]} icon={storePin} draggable={false} />}
                  </MapContainer>
                  <div className="absolute left-2 top-2 z-[1000] flex w-10 flex-col items-center rounded-lg border border-slate-200 bg-white text-slate-800 shadow-md">
                    <button type="button" onClick={() => map?.zoomIn()} aria-label={t.storeZoomIn} className="flex h-10 w-10 items-center justify-center rounded-t-lg text-lg font-medium hover:bg-slate-100">+</button>
                    <span className="flex h-8 w-10 items-center justify-center border-y border-slate-200 text-xs font-medium" aria-live="polite">{zoom}x</span>
                    <button type="button" onClick={() => map?.zoomOut()} aria-label={t.storeZoomOut} className="flex h-10 w-10 items-center justify-center rounded-b-lg text-lg font-medium hover:bg-slate-100">−</button>
                  </div>
                  <div className="absolute right-2 top-2 z-[1000] flex rounded-lg border border-slate-200 bg-white p-1 shadow-md" role="group" aria-label={t.storeMapView}>
                    <button type="button" onClick={() => setMapType("map")} aria-pressed={mapType === "map"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "map" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><FiMap size={14} aria-hidden="true" />{t.storeDefaultView}</button>
                    <button type="button" onClick={() => setMapType("satellite")} aria-pressed={mapType === "satellite"} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${mapType === "satellite" ? "bg-[#236DB4] text-white" : "text-slate-700 hover:bg-slate-100"}`}><LuSatellite size={15} aria-hidden="true" />{t.storeSatelliteView}</button>
                  </div>
                </div>
                <fieldset disabled={isViewOnly} className="mt-4 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block sm:col-span-1">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="block text-sm font-medium">{t.storeName}</span>
                      <span className="text-xs text-slate-400">
                        {storeName.length}/255
                      </span>
                    </div>
                    <input
                      type="text"
                      maxLength={255}
                      value={storeName}
                      onChange={(event) => {
                        const val = event.target.value;
                        setStoreName(val);
                        if (storeNameError) setStoreNameError("");
                        if (val.trim().length > 255) {
                          setStoreNameError(t.storeNameMaxLength || "Store name must not exceed 255 characters.");
                        }
                      }}
                      className={`h-10 w-full rounded-lg border px-3 text-sm outline-none transition-colors ${
                        storeNameError
                          ? highContrast
                            ? "border-red-400 bg-black text-white focus:ring-2 focus:ring-red-400"
                            : dark
                              ? "border-red-500 bg-slate-800 text-white focus:ring-2 focus:ring-red-500"
                              : "border-red-500 bg-white text-slate-800 focus:ring-2 focus:ring-red-500"
                          : `focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`
                      }`}
                    />
                    {storeNameError ? (
                      <p role="alert" className="mt-1.5 text-xs font-medium text-red-500">
                        {storeNameError}
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs text-slate-400">
                        {t.storeNameNote || "Note: Maximum 255 characters allowed."}
                      </p>
                    )}
                  </label>
                  <div className="min-w-0 sm:col-span-1">
                    <label htmlFor="store-status-select" className="mb-1 block text-sm font-medium">{t.storeStatus || "Status"}</label>
                    <ReactSelector
                      options={statusOptions}
                      value={statusOptions.find((option) => option.value === storeStatus) || null}
                      onChange={(option) => { if (!isViewOnly && option) setStoreStatus(option.value); }}
                      isDisabled={isViewOnly}
                      isClearable={false}
                      height="40px"
                      borderRadius="8px"
                      fontSize="14px"
                      borderColor={highContrast ? "#ffffff" : dark ? "#475569" : "#cbd5e1"}
                      dataTestId="store-status-select"
                    />
                  </div>
                  <div className={`sm:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 ${fieldClass}`}>
                    <span className="text-sm font-medium">{t.storeLocationMethod}</span>
                    <div
                      role="radiogroup"
                      aria-label={t.storeLocationMethod}
                      className={`relative inline-flex items-center rounded-full p-1 text-xs sm:text-sm font-medium border ${
                        highContrast
                          ? "border-white bg-black"
                          : dark
                            ? "border-slate-600 bg-slate-800"
                            : "border-slate-200 bg-slate-100"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute inset-y-1 w-[calc(50%-4px)] rounded-full transition-transform duration-200 ease-in-out ${
                          highContrast
                            ? "bg-white"
                            : "bg-[#236DB4] shadow-sm"
                        } ${
                          isRTL
                            ? `right-1 ${locationMode === "coordinates" ? "-translate-x-full" : "translate-x-0"}`
                            : `left-1 ${locationMode === "coordinates" ? "translate-x-full" : "translate-x-0"}`
                        }`}
                      />
                      <button
                        type="button"
                        role="radio"
                        aria-checked={locationMode === "address"}
                        onClick={() => setLocationMode("address")}
                        className={`relative z-10 min-w-[90px] rounded-full px-3.5 py-1.5 text-center transition-colors duration-200 ${
                          locationMode === "address"
                            ? highContrast
                              ? "text-black font-semibold"
                              : "text-white font-semibold"
                            : highContrast
                              ? "text-white"
                              : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                        }`}
                      >
                        {t.storeAddress}
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={locationMode === "coordinates"}
                        onClick={() => setLocationMode("coordinates")}
                        className={`relative z-10 min-w-[90px] rounded-full px-3.5 py-1.5 text-center transition-colors duration-200 ${
                          locationMode === "coordinates"
                            ? highContrast
                              ? "text-black font-semibold"
                              : "text-white font-semibold"
                            : highContrast
                              ? "text-white"
                              : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                        }`}
                      >
                        {t.storeCoordinates}
                      </button>
                    </div>
                  </div>
                  {locationMode === "address" ? (
                    <div className="sm:col-span-2">
                      <label htmlFor="store-address-search" className="mb-1 block text-sm font-medium">{t.searchStoreAddress}</label>
                      <input id="store-address-search" type="text" autoComplete="off" value={addressQuery} onChange={(event) => { reverseAbortRef.current?.abort(); setLocationOrigin("manual"); setLatitude(""); setLongitude(""); setAddressFields(emptyStoreAddress); setAddressQuery(event.target.value); setAddressResults([]); setAddressSearchError(""); }} placeholder={t.storeAddressSearchPlaceholder} className={`h-10 w-full min-w-0 rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                      {addressSearching && <p className="mt-2 text-sm text-slate-500">{t.searchingStoreAddress}</p>}
                      {addressSearchError && <p role="alert" className="mt-2 text-sm text-red-500">{addressSearchError}</p>}
                      {addressResults.length > 0 && (
                        <ul className={`mt-2 max-h-40 overflow-y-auto rounded-lg border ${fieldClass}`} aria-label={t.storeAddressResults}>
                          {addressResults.map((result) => (
                            <li key={result.place_id} className="border-b border-inherit last:border-b-0">
                              <button type="button" onClick={() => selectAddress(result)} className="w-full px-3 py-2 text-start text-sm hover:bg-[#236DB4]/10">{result.display_name}</button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {!addressSearching && !addressSearchError && addressQuery.trim() && addressSearchCache.get(`${langMode}:${addressQuery.trim().toLowerCase()}`)?.length === 0 && <p className="mt-2 text-sm text-slate-500">{t.noOmanAddressesFound}</p>}
                      <h3 className="mb-2 mt-4 text-sm font-semibold">{t.storeAddress}</h3>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeGovernorate}</span>
                          <input type="text" list="oman-governorates" value={addressFields.governorate} onChange={(event) => updateAddressField("governorate", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                          <datalist id="oman-governorates">{omanGovernorates.map((name) => <option key={name} value={name} />)}</datalist>
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeWilayat}</span>
                          <input type="text" value={addressFields.wilayat} onChange={(event) => updateAddressField("wilayat", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeCity}</span>
                          <input type="text" value={addressFields.city} onChange={(event) => updateAddressField("city", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeArea}</span>
                          <input type="text" value={addressFields.area} onChange={(event) => updateAddressField("area", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeStreet}</span>
                          <input type="text" value={addressFields.street} onChange={(event) => updateAddressField("street", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-sm font-medium">{t.storeBuilding}</span>
                          <input type="text" value={addressFields.building} onChange={(event) => updateAddressField("building", event.target.value)} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                        </label>
                      </div>
                      <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs">
                        {reverseGeocoding && (
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                            {t.fetchingAddressFromMap || "Fetching address from map..."}
                          </span>
                        )}
                        {!reverseGeocoding && resolvingLocation && (
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                            <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                            {t.detectingCoordinates || "Detecting coordinates..."}
                          </span>
                        )}
                        {!reverseGeocoding && !resolvingLocation && hasLocation && (
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            {locationOrigin === "map"
                              ? `${t.addressFilledFromMap || "Address filled from map"}: ${lat.toFixed(6)}, ${lng.toFixed(6)}`
                              : `${t.autoDetectedCoordinates || "Coordinates automatically detected"}: ${lat.toFixed(6)}, ${lng.toFixed(6)}`}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="block">
                        <span className="mb-1 block text-sm font-medium">{t.storeLatitude}</span>
                        <input type="number" min="-90" max="90" step="any" value={latitude} onChange={(event) => { setLatitude(event.target.value); setAddressFields(emptyStoreAddress); setAddressQuery(""); setLocationOrigin("coordinates"); reverseAbortRef.current?.abort(); }} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-sm font-medium">{t.storeLongitude}</span>
                        <input type="number" min="-180" max="180" step="any" value={longitude} onChange={(event) => { setLongitude(event.target.value); setAddressFields(emptyStoreAddress); setAddressQuery(""); setLocationOrigin("coordinates"); reverseAbortRef.current?.abort(); }} className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-[#236DB4] ${fieldClass}`} />
                      </label>
                    </>
                  )}
                </fieldset>
                {createError && (
                  <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-950/50 dark:text-red-300">
                    {createError}
                  </div>
                )}
              </div>
              <div className={`flex shrink-0 flex-wrap justify-end gap-2 border-t px-4 py-3 sm:px-5 ${highContrast ? "border-white" : dark ? "border-slate-700" : "border-slate-200"}`}>
                <CommonButton colorIntent="danger" onClick={handleClose} theme={theme} variant={isViewOnly ? "primary" : "outline"} className="!h-10 !min-h-[40px]">{isViewOnly ? (t.close || t.cancel || "Close") : t.cancel}</CommonButton>
                {!isViewOnly && (
                  <CommonButton colorIntent="success" onClick={handleCreate} disabled={creating || reverseGeocoding} theme={theme} variant="primary" className="!h-10 !min-h-[40px]">
                    {creating ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        {isEditing ? (t.updateStore || "Update") : t.createStore}
                      </span>
                    ) : (
                      isEditing ? (t.updateStore || "Update") : t.createStore
                    )}
                  </CommonButton>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateStoreModal;
